import { useEffect } from "react";
import { useStickToBottom } from "use-stick-to-bottom";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import usePrevious from "hooks/usePrevious";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";

import { AiChatMessage } from "../types";
import Thinking from "../Thinking";
import styles from "./styles.module.css";

type AiChatMessagesProps = {
  messages: AiChatMessage[];
};

const AiChatMessages = ({ messages }: AiChatMessagesProps) => {
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "instant",
    resize: "smooth",
  });

  const userMessagesCount = messages.filter((m) => m.role === "user").length;
  const prevUserMessagesCount = usePrevious(userMessagesCount);

  useEffect(() => {
    if (prevUserMessagesCount !== undefined && userMessagesCount > prevUserMessagesCount) {
      scrollToBottom();
    }
  }, [userMessagesCount, prevUserMessagesCount, scrollToBottom]);

  return (
    <div ref={scrollRef} className={styles.scrollContainer}>
      <Box ref={contentRef} direction="column" gap="xx-large" padding="x-large">
        {messages.map((message) => {
          if (message.role === "user") {
            return (
              <Box key={message.id} gap="medium" justify="end" align="end">
                <Box className={styles.userMessageContent}>
                  {message.parts.map((part, index) => {
                    if (part.type === "text") {
                      return (
                        <Typography key={index} tag="p" variant="p-body3-multiline">
                          {part.text}
                        </Typography>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Box>
            );
          }

          return (
            <Box
              key={message.id}
              direction="column"
              gap="x-large"
              className={styles.assistantMessage}
            >
              {message.parts.map((part, index) => {
                // TODO: Add support for other part types
                // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
                switch (part.type) {
                  case "reasoning":
                    return (
                      <Thinking
                        key={index}
                        text={part.text}
                        isStreaming={part.state === "streaming"}
                      />
                    );
                  case "text":
                    return <MarkdownAssistantPanel key={index}>{part.text}</MarkdownAssistantPanel>;
                  default:
                    return null;
                }
              })}
            </Box>
          );
        })}
      </Box>
    </div>
  );
};

export default AiChatMessages;
